<template>
  <v-container fluid class="emp-assignments-view">
    <div class="d-flex flex-column employee-card-path-to-readiness">
      <EmployeePageHeader
        v-if="isNewLayoutEnabled"
        :employeeData="employeeData"
        :loading="loading"
        @employee-updated="handleEmployeeUpdate"
        @employee-status-changed="handleEmployeeStatusChanged"
        @refresh-employee-data="handleRefreshEmployeeData"
        @recalc-readiness="onEmployeeReadiness"
      />
      <div>
        <EmployeePositionHistoryTab
          :dataSource="emoloyeeAssignment"
          :topLevel="'position'"
          :removeBorder="false"
          :storePersistFilters="true"
          :storeFiltersKeyPrefix="'employeeAssignments'"
          @appliedFilter="handleFilterChange"
          @searchTextData="handleSearchChange"
          @getEmployeeDetails="getEmployeesAssignments"
        />
        <ExPagination
          v-if="
            emoloyeeAssignment.length > 0 &&
            pagination.total > pagination.perPage
          "
          :pagination="pagination"
          @onRequest="handlePaginationRequest"
        />
      </div>
    </div>
  </v-container>
</template>

<script>
import EmployeePositionHistoryTab from "@components/EmployeeAssignments/EmployeePositionHistoryTab.vue";
import EmployeePageHeader from "@components/common/EmployeePageHeader.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import { getEmployeeAssignments } from "@/api";
import { useEmployeeStore } from "@/store/pinia/employee";
export default {
  name: "EmployeeAssignmentView",
  components: {
    EmployeePositionHistoryTab,
    EmployeePageHeader,
    ExPagination,
  },
  setup() {
    const employeeStore = useEmployeeStore();
    return {
      employeeStore,
    };
  },
  data() {
    return {
      showNotification: true,
      dataList: [],
      emoloyeeAssignment: [],
      employeeData: null,
      loading: {
        readinessBar: false,
      },
      pagination: {
        page: 1,
        perPage: 10,
        total: 0,
      },
      appliedFilters: [],
      search: "",
    };
  },
  computed: {
    isNewLayoutEnabled() {
      return this.$store.getters["featureFlags/isNewDesign"];
    },
  },
  watch: {
    "$route.params.id": {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.$nextTick(async () => {
            const success = await this.handleRefreshEmployeeData();
            if (!success) {
              this.$router.replace({ name: "NotFound" });
            }
          });
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    const storedAccountID = localStorage.getItem("currentAccountID");
    if (
      storedAccountID &&
      storedAccountID !== "null" &&
      storedAccountID !== "undefined"
    ) {
      this.accountID = parseInt(storedAccountID, 10);
    }
    this.$loader.show();
    const success = await this.getEmployeeData();
    if (!success) {
      this.$loader.hide();
      this.$router.replace({ name: "NotFound" });
      return;
    }
    await this.getEmployeesAssignments();
    this.$loader.hide();
  },
  methods: {
    async getEmployeeData() {
      try {
        const empId = this.$route.params.id || this.$route.query.employeeId;
        if (!empId) {
          console.error("No employee ID found in route parameters or query");
          return false;
        }

        let employeeData = this.$store.getters["employeeStore/currentEmployee"];

        if (!employeeData || !employeeData.id || employeeData.id != empId) {
          try {
            employeeData = await this.employeeStore.fetchCurrentEmployee(
              empId,
              true
            );

            if (employeeData) {
              this.$store.commit(
                "employeeStore/setCurrentEmployee",
                employeeData
              );
            }
          } catch (error) {
            console.error("Error fetching employee data:", error);
            // If it's a 404 error, return false
            if (error.response && error.response.status === 404) {
              return false;
            }
          }
        }

        if (employeeData && employeeData.id) {
          this.employeeData = employeeData;
          return true;
        } else {
          console.error("Failed to fetch employee data for ID:", empId);
          return false;
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
        return false;
      }
    },
    async handleRefreshEmployeeData() {
      const empId = this.$route.params.id || this.$route.query.employeeId;
      if (empId) {
        try {
          const employeeData = await this.employeeStore.fetchCurrentEmployee(
            empId,
            true
          );
          if (employeeData && employeeData.id) {
            this.$store.commit(
              "employeeStore/setCurrentEmployee",
              employeeData
            );
            this.employeeData = employeeData;
            return true;
          } else {
            console.error("Failed to fetch employee data for ID:", empId);
            return false;
          }
        } catch (error) {
          console.error("Error refreshing employee data:", error);
          // If it's a 404 error, return false
          if (error.response && error.response.status === 404) {
            return false;
          }
          return false;
        }
      }
      return false;
    },
    async getEmployeesAssignments() {
      const empId = this.$route.params.id || this.$route.query.employeeId;

      if (!this.accountID || isNaN(this.accountID)) {
        console.error(
          "Cannot fetch employee assignments: Invalid account ID:",
          this.accountID
        );
        this.emoloyeeAssignment = [];
        return;
      }

      this.$loader.show();
      try {
        const statusFilters =
          this.appliedFilters.length > 0 ? this.appliedFilters : null;
        const { data } = await getEmployeeAssignments(
          this.accountID,
          empId,
          this.pagination.perPage,
          (this.pagination.page - 1) * this.pagination.perPage,
          statusFilters,
          this.search || null
        );

        if (data) {
          if (data.results && data.results.length > 0) {
            this.pagination.total = data.count || 0;
            this.emoloyeeAssignment = this.transformData(data.results);
          } else {
            this.pagination.total = 0;
            this.emoloyeeAssignment = [];
          }
        }
      } catch (error) {
        console.error("Error fetching employee assignments:", error);
        this.emoloyeeAssignment = [];
        this.pagination.total = 0;
      }
      this.$loader.hide();
    },
    transformData(dataArray) {
      const result = [];
      const today = new Date();

      dataArray.forEach((data) => {
        const isNew =
          new Date(data.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 >
          today.getTime();

        const mainObject = {
          id: data.id,
          parentId: -1,
          data: {
            type: data.assignment_type,
            commitmentLevel: data.commitment_level,
            assignmentType: data.assignment_type,
            importanceLevel: data.importance,
            importanceSelector: true,
            totalSteps: 3,
            code: data.position_code,
            name: data.position_name,
            assignedOn: data.assigned_on,
            removedOn: data.removed_on,
            assignedBy: data.assigned_by,
            removedBy: data.removed_by,
            reason: data.reason,
            isNew: isNew,
            isAccepted: false,
            effectiveOn: data.effective_on,
            effectiveBy: data.effective_by,
            hasRoles: data.role_assignment_count > 0,
            roleCount: data.role_assignment_count,
          },
          assignment_id: data.id,
          status: data.status,
          readiness: data.current_readiness,
          hasItems: data.role_assignment_count > 0,
          effectiveData: {},
          assignedData: {},
          removedData: {},
          reasonData: {},
        };
        result.push(mainObject);
      });

      const commitmentOrder = {
        Primary: 1,
        "Preparing for": 2,
        Backup: 3,
      };

      result.sort((a, b) => {
        const aCommit = a.data?.commitmentLevel || "";
        const bCommit = b.data?.commitmentLevel || "";
        return (
          (commitmentOrder[aCommit] || 99) - (commitmentOrder[bCommit] || 99)
        );
      });

      return result;
    },

    hideNotification() {
      this.showNotification = false;
    },
    handleEmployeeUpdate() {
      this.employeeData = this.$store.getters["employeeStore/currentEmployee"];
      this.$emit("employeeUpdated");
    },
    async handleEmployeeStatusChanged() {
      this.employeeData = this.$store.getters["employeeStore/currentEmployee"];
      await this.getEmployeesAssignments();
      this.$emit("employeeStatusChanged");
    },
    async onEmployeeReadiness() {
      const empId = this.$route.params.id;
      if (empId) {
        this.$parent.$refs?.employeeSideBar?.recalculateEmployeeReadiness(
          empId
        );

        this.employeeData =
          this.$store.getters["employeeStore/currentEmployee"];
      }
    },

    async handlePaginationRequest(paginationData) {
      this.pagination.page = paginationData.page;
      this.$loader.show();
      try {
        await this.getEmployeesAssignments();
      } catch (error) {
        console.error("Error loading paginated data:", error);
      } finally {
        this.$loader.hide();
      }
    },

    async handleFilterChange(filters) {
      // Always update filters and refresh data when filters change
      // This ensures clearing filters properly refreshes the view
      const filtersChanged =
        JSON.stringify(this.appliedFilters) !== JSON.stringify(filters);
      if (filtersChanged) {
        this.appliedFilters = filters || [];
        this.pagination.page = 1;
        await this.getEmployeesAssignments();
      }
    },

    async handleSearchChange(searchTerm) {
      this.search = searchTerm;
      this.pagination.page = 1;
      await this.getEmployeesAssignments();
    },
  },
};
</script>

<style scoped>
.emp-assignments-view .main-heading {
  font-family: Inter;
  font-weight: 500;
  font-size: 30px;
  line-height: 38px;
  text-align: left;
  color: #101828;
  padding-bottom: 20px;
}
.emp-assignment {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  margin-bottom: 25px;
}
.emp-assignments-view .list-section {
  border: 1px solid #eaecf0;
  border-radius: 8px;
  margin-bottom: 24px;
}
.emp-assignments-view .notifi-left {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}
.emp-assignments-view .notification-title {
  font-family: Inter;
  font-size: 16px;
  font-weight: 500;
  color: #344054;
  text-align: left;
}
.emp-assignments-view .notification-description {
  font-family: Inter;
  font-size: 16px;
  font-weight: 400;
  color: #475467;
  text-align: left;
}
</style>
